import type { ReactNode } from 'react'


/**
 * Inline SVG rather than an emoji or a glyph, so the stroke weight matches the
 * rest of the interface and the mark renders identically on every machine.
 */
/** Three lines, the menu button on a phone (FR-67). */
export function MenuIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  )
}

export function SignOutIcon({ size = 17 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* The doorway, open on the side the arrow leaves through. */}
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      {/* The arrow going out of it. */}
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

/**
 * The two states of a show-password control.
 *
 * `shown` draws the crossed out eye, because the icon shows what pressing it
 * will do next rather than what the field is doing now. A plain eye while the
 * password is already visible reads as an offer to reveal something that is
 * already there.
 */
export function EyeIcon({ shown = false, size = 17 }: { shown?: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shown ? (
        <>
          {/* The eye, broken into the parts a slash leaves visible. */}
          <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19" />
          <path d="M6.6 6.6A18.2 18.2 0 0 0 2 12s3 8 10 8a9.7 9.7 0 0 0 5.4-1.6" />
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          <line x1="2" y1="2" x2="22" y2="22" />
        </>
      ) : (
        <>
          <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  )
}

/* ---------- Iconic's panel (STE-997) ---------- */

function Stroke({ size = 17, children }: { size?: number; children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {children}
    </svg>
  )
}

/** Iconic's mark: a four pointed star, filled so it reads at 12px. */
export function SparkleIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M12 2c.6 4.9 3.1 7.4 8 8-4.9.6-7.4 3.1-8 8-.6-4.9-3.1-7.4-8-8 4.9-.6 7.4-3.1 8-8Z" />
    </svg>
  )
}
/** The chat list, as a panel beside the chat. */
export const HistoryIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="9" y1="4" x2="9" y2="20" /></Stroke>
)
export const PlusIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Stroke>
)
export const MinimiseIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><line x1="6" y1="18" x2="18" y2="18" /></Stroke>
)
export const MaximiseIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></Stroke>
)
export const RestoreIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /></Stroke>
)
export const CloseIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></Stroke>
)
export const PencilIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><path d="M4 20h4L19 9l-4-4L4 16v4Z" /></Stroke>
)
export const TrashIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></Stroke>
)
export const SendIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><line x1="12" y1="19" x2="12" y2="5" /><polyline points="6 11 12 5 18 11" /></Stroke>
)
export const StopIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
)
export const CheckIcon = ({ size }: { size?: number }) => (
  <Stroke size={size}><polyline points="5 12 10 17 19 7" /></Stroke>
)
